import {
  HttpHandler,
  HttpHeaders,
  HttpInterceptor,
  HttpRequest,
  HttpXsrfTokenExtractor,
} from '@angular/common/http';
import { Injectable, Injector, runInInjectionContext } from '@angular/core';

import { EMPTY, Observable, catchError, switchMap, throwError } from 'rxjs';
import { environment } from '@environments/environment';
import { AuthService } from '../services/auth.service';
import {
  ACCESS_TOKEN_EXPIRED,
  SESSION_EXPIRED_MESSAGE,
  UNAUTHORIZED_DETAIL_MESSAGE,
  UNAUTHORIZED_MESSAGE,
} from '@app/shared/utils/layout-util';
import { API_NAME } from '@app/shared/utils/api-util';
import { LoginsessionService } from '@app/shared/services/login-session/loginsession.service';
import { MatDialog } from '@angular/material/dialog';
import { ErrorMessageComponent } from '@app/shared/components/error-message/error-message.component';

@Injectable({
  providedIn: 'root',
})
export class BayerInterceptor implements HttpInterceptor {
  isInvalidToken: boolean = false;
  isAccessTokenGenerated: boolean = false;
  constructor(
    private readonly httpxsrToken: HttpXsrfTokenExtractor,
    private readonly authService: AuthService,
    private readonly loginSessionService: LoginsessionService,
    private readonly injector: Injector,
  ) {}

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<any> {
    if (this.isInvalidToken) {
      return EMPTY;
    }
    this.isAccessTokenGenerated &&
      (this.isAccessTokenGenerated = !this.isAccessTokenGenerated);

    if (!req.url.endsWith('.json') && !req.url.endsWith('.svgr')) {
      const reqUrl = `${environment?.apiUrl}/${req.url}`;
      req = this.getRequest(req, reqUrl);
    }

    return next.handle(req).pipe(
      catchError((error: any) => {
        const url = req.url?.toLocaleUpperCase();
        if (
          error?.status === 401 &&
          (error?.error?.message?.toLocaleLowerCase() ===
            UNAUTHORIZED_MESSAGE?.toLocaleLowerCase() ||
            error?.error?.message?.toLowerCase() ===
              UNAUTHORIZED_DETAIL_MESSAGE?.toLocaleLowerCase())
        ) {
          this.sessionExpiredPopup(error, error?.error?.message as string);
        } else if (error?.status === 401) {
          const count = this.loginSessionService.getExtensionCount();
          const status = this.loginSessionService.getConfirmToken();
          if (
            error?.error?.message?.toLowerCase() ===
              ACCESS_TOKEN_EXPIRED?.toLocaleLowerCase() &&
            count > 0 &&
            status &&
            !url.includes(API_NAME.user_logout?.toLocaleLowerCase())
          ) {
            if (this.isAccessTokenGenerated) {
              return next.handle(req);
            }
            return this.generateNewToken(req, next);
          }

          return this.sessionExpiredPopup(error, SESSION_EXPIRED_MESSAGE);
        }

        if (error?.status === 403) {
          runInInjectionContext(this.injector, () => {
            const dialog = this.injector.get(MatDialog);
            dialog.closeAll();
            dialog.open(ErrorMessageComponent, {
              data: error?.error?.message ?? 'translation.common.errorMessage',
              disableClose: true,
            });
          });
          return throwError(() => error);
        }

        if (error?.status === 500) {
          console.error(error);
        }
        return throwError(() => error);
      }),
    );
  }

  sessionExpiredPopup(error: any, message: string) {
    if (!this.isInvalidToken) {
      alert(message);
    }
    this.isInvalidToken = true;
    this.authService.clearStorage();
    return throwError(() => error);
  }

  generateNewToken(req: HttpRequest<any>, next: HttpHandler) {
    return this.authService.generateNewToken().pipe(
      switchMap((token: any) => {
        if (token?.access_token) {
          this.isAccessTokenGenerated = true;
          req = this.getRequest(req, req.url);
          this.loginSessionService.updateConfirmDate('');
          this.loginSessionService.updateConfirmStatus(false);
          return next.handle(req);
        }
        return this.sessionExpiredPopup(token, SESSION_EXPIRED_MESSAGE);
      }),
      catchError((error) => {
        return this.sessionExpiredPopup(error, SESSION_EXPIRED_MESSAGE);
      }),
    );
  }

  getRequest(request: HttpRequest<any>, url: any) {
    const csrfToken: string = this.httpxsrToken.getToken() as string;
    let headers = new HttpHeaders();

    if (csrfToken) {
      headers = new HttpHeaders({ 'X-CSRF-TOKEN': csrfToken });
    }

    if (request.headers.has('isContentType')) {
      const acceptFileFormat: any = request?.headers?.get('Accept');
      headers = new HttpHeaders({ 'X-CSRF-TOKEN': csrfToken }).append(
        'Accept',
        acceptFileFormat,
      );
    }

    return request.clone({
      withCredentials: true,
      url,
      headers,
    });
  }
}
